import { EntityType } from '@frontend/common';
import { CheckboxInput } from '@frontend/components/basic-forms';
import { ActionModal } from '@frontend/components/modal';
import { findPlatformFunctionality } from '@frontend/functionality/utils';
import { useModals } from '@frontend/modal';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { useLicenseRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import { TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName, TransactionTriggerName, TransactionUserType } from '@frontend/transaction/types';
import { UserSelectV2 } from '@frontend/user/select';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RequireProofOfIdentityLabel } from '../../../wizard-rendering/messages/proof-of-identity.messages';

interface CancelTransactionModalProps {
    transaction?: Transaction;
    transactions?: Transaction[];
    onSubmitCallback?: () => void;
}

export const CancelTransactionModal = (props: CancelTransactionModalProps) => {
    const intl = useIntl();
    const { closeTopModal } = useModals();
    const { functionalities } = useLicenseRepository();
    const transaction = props.transaction ?? props.transactions?.[0] ?? null;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [transactionUserIds, setTransactionUserIds] = useState<string[]>([]);
    const [selectedRemoverId, setSelectedRemoverId] = useState<string | null>(null);
    const [showAllUsers, setShowAllUsers] = useState(false);
    const [requireProofOfIdentity, setRequireProofOfIdentity] = useState(false);

    const isPickUpPending = transaction?.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING;

    useEffect(() => {
        if (!isPickUpPending || !transaction) return;

        let cancelled = false;
        void TransactionUserClient.fetchTransactionUsers({ transaction_id: transaction.id })
            .then((response) => {
                if (cancelled) return;

                const userIds = Array.from(new Set(response.results.map((transactionUser) => transactionUser.user_id).filter((userId) => !!userId)));
                const fallbackUserId = transaction.sender_ids?.[0] ?? transaction.user_id ?? null;
                const defaultUserId = userIds[0] ?? fallbackUserId;
                const subset = defaultUserId && !userIds.includes(defaultUserId) ? [...userIds, defaultUserId] : userIds;

                setTransactionUserIds(subset);
                setSelectedRemoverId(defaultUserId);
            })
            .catch(() => {
                if (cancelled) return;

                const fallbackUserId = transaction.sender_ids?.[0] ?? transaction.user_id ?? null;
                setTransactionUserIds(fallbackUserId ? [fallbackUserId] : []);
                setSelectedRemoverId(fallbackUserId);
            });

        void PackageClient.fetchPackages({
            transaction_id: transaction.id,
            type: PackageType.PICK_UP
        })
            .then((response) => {
                if (cancelled) return;
                const pendingPickUpPackage = response.results.find((pkg) => pkg.state === PackageStateName.PACKAGE_PICK_UP_PENDING);
                setRequireProofOfIdentity(pendingPickUpPackage?.require_proof_of_identity ?? false);
            })
            .catch(() => {
                if (!cancelled) setRequireProofOfIdentity(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isPickUpPending, transaction]);

    const removerSubset = useMemo(() => {
        if (showAllUsers || transactionUserIds.length === 0) return undefined;
        return transactionUserIds;
    }, [showAllUsers, transactionUserIds]);

    const submitDisabled = isSubmitting || (isPickUpPending && !selectedRemoverId);

    if (!transaction) return null;

    return (
        <ActionModal
            title={
                <FormattedMessage
                    id='action-modal.cancel-transaction-title'
                    description='Title shown in the transaction cancellation modal'
                    defaultMessage='Cancel transaction'
                />
            }
            cancel={{
                onCancel: () => closeTopModal()
            }}
            confirm={{
                disabled: submitDisabled,
                isLoading: isSubmitting,
                onConfirm: async () => {
                    setIsSubmitting(true);
                    try {
                        if (isPickUpPending) {
                            if (!selectedRemoverId) {
                                ToastUtil.warning(
                                    intl.formatMessage({
                                        id: 'action-modal.cancel-transaction.remover-required-warning',
                                        description: 'Warning shown when no remover is selected before cancelling a pick-up pending transaction',
                                        defaultMessage: 'Please select who will remove the package.'
                                    })
                                );
                                return;
                            }

                            const packages = await PackageClient.fetchPackages({
                                transaction_id: transaction.id,
                                type: PackageType.PICK_UP
                            });

                            const pendingPickUpPackage = packages.results.find((pkg) => pkg.state === PackageStateName.PACKAGE_PICK_UP_PENDING);
                            if (!pendingPickUpPackage) {
                                ToastUtil.warning(
                                    intl.formatMessage({
                                        id: 'action-modal.cancel-transaction.pick-up-package-not-found',
                                        description: 'Warning shown when no pending pick-up package is found while cancelling a pick-up pending transaction',
                                        defaultMessage: 'No pending pick-up package was found for this transaction.'
                                    })
                                );
                                return;
                            }

                            const functionality =
                                transaction.application && transaction.feature
                                    ? findPlatformFunctionality(functionalities, transaction.application, transaction.feature)
                                    : undefined;
                            if (!functionality) {
                                ToastUtil.warning(
                                    intl.formatMessage({
                                        id: 'action-modal.cancel-transaction.functionality-not-found',
                                        description:
                                            'Warning shown when no matching functionality is found while creating a removal package during cancellation',
                                        defaultMessage: 'No matching functionality was found for this transaction.'
                                    })
                                );
                                return;
                            }

                            const functionalitySettings = await SettingClient.fetchSettings({
                                entity_type: EntityType.FUNCTIONALITY,
                                entity_id: functionality.id
                            });
                            const removePackageWorkflowId = functionalitySettings.results.find(
                                (setting) => setting.key === SettingKey.REMOVE_PACKAGE_WORKFLOW_ID
                            )?.value;

                            if (typeof removePackageWorkflowId !== 'string' || removePackageWorkflowId.length === 0) {
                                ToastUtil.warning(
                                    intl.formatMessage({
                                        id: 'action-modal.cancel-transaction.remove-workflow-missing',
                                        description:
                                            'Warning shown when the remove package workflow id setting is missing while creating a removal package during cancellation',
                                        defaultMessage: 'The remove package workflow is not configured for this functionality.'
                                    })
                                );
                                return;
                            }

                            await TransactionUserClient.postTransactionUser(transaction.account_id, transaction.id, {
                                user_id: selectedRemoverId,
                                type: TransactionUserType.REMOVER
                            });

                            await PackageClient.postPackage(transaction.account_id, transaction.id, {
                                iot_id: pendingPickUpPackage.iot_id,
                                product_id: pendingPickUpPackage.product_id,
                                equipment_id: pendingPickUpPackage.equipment_id,
                                colli_id: pendingPickUpPackage.colli_id,
                                spot_id: pendingPickUpPackage.spot_id ?? undefined,
                                module_id: pendingPickUpPackage.module_id ?? undefined,
                                slot_id: pendingPickUpPackage.slot_id ?? undefined,
                                quantity: pendingPickUpPackage.quantity,
                                application: pendingPickUpPackage.application,
                                feature: pendingPickUpPackage.feature,
                                priority: pendingPickUpPackage.priority,
                                budget_id: pendingPickUpPackage.budget_id,
                                cost_center_id: pendingPickUpPackage.cost_center_id,
                                blocking: pendingPickUpPackage.blocking,
                                dependent_package_id: pendingPickUpPackage.id,
                                require_proof_of_identity: requireProofOfIdentity,
                                workflow_id: removePackageWorkflowId,
                                type: PackageType.REMOVAL
                            });
                        }

                        await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                            trigger: TransactionTriggerName.TRANSACTION_CANCEL
                        });

                        if (props.onSubmitCallback) {
                            props.onSubmitCallback();
                        }
                        ToastUtil.success(
                            intl.formatMessage({
                                id: 'action-modal.cancel-transaction.success',
                                description: 'Toast shown when a transaction is cancelled successfully',
                                defaultMessage: 'Cancelled transaction successfully.'
                            })
                        );
                        closeTopModal();
                    } finally {
                        setIsSubmitting(false);
                    }
                }
            }}>
            <div className='text-brand-muted justify-start self-stretch text-base font-medium'>
                <FormattedMessage
                    id='action-modal.cancel-transaction-message'
                    description='Explanatory text shown in the transaction cancellation modal'
                    defaultMessage='Are you sure you want to cancel this transaction? Based on your workflow settings, this action may trigger notifications to the senders and/or receivers.'
                />
            </div>
            {isPickUpPending && (
                <div className='border-border-default mt-2 flex w-full flex-col gap-4 border-t pt-4'>
                    <div className='flex w-full items-start gap-3'>
                        <div className='flex-1'>
                            <UserSelectV2
                                id='cancel-transaction-remover'
                                label={
                                    <FormattedMessage
                                        id='action-modal.cancel-transaction.remover-label'
                                        description='Label of the remover user select in the cancel transaction modal for pick-up pending transactions'
                                        defaultMessage='Removal by'
                                    />
                                }
                                value={selectedRemoverId}
                                onChange={setSelectedRemoverId}
                                required
                                subset={removerSubset}
                            />
                        </div>
                        <div className='pt-9'>
                            <CheckboxInput
                                id='cancel-transaction-show-all-users'
                                label={
                                    <FormattedMessage
                                        id='action-modal.cancel-transaction.show-all-users'
                                        description='Label for the toggle that expands the remover user select options to all users in the cancel transaction modal'
                                        defaultMessage='Show all users'
                                    />
                                }
                                value={showAllUsers}
                                onChange={setShowAllUsers}
                            />
                        </div>
                    </div>
                    {!selectedRemoverId && (
                        <div className='text-status-danger -mt-2 text-sm font-medium'>
                            <FormattedMessage
                                id='action-modal.cancel-transaction.remover-required'
                                description='Validation message shown when no remover is selected in the cancel transaction modal'
                                defaultMessage='This field is required.'
                            />
                        </div>
                    )}
                    <CheckboxInput
                        id='cancel-transaction-require-proof-of-identity'
                        label={<RequireProofOfIdentityLabel />}
                        value={requireProofOfIdentity}
                        onChange={setRequireProofOfIdentity}
                    />
                </div>
            )}
        </ActionModal>
    );
};
